import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Volume2, VolumeX } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";

const DEFAULTS = {
  enabled: true,
  version: "2026.12",
  founder: "VOSURI NAVYA",
  cofounder: "SURENDRA YEDDULA",
  finalMessage: "YOUR DREAM COMES TRUE TODAY.\nSTART YOUR WEBSITE HERE.",
  signature: "NAVYA VOSURI × SURENDRA YEDDULA",
};

const STORAGE_KEY = "naviCareLaunchCompleted";
const VERSION_KEY = "naviCareLaunchVersion";

function normalize(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
}
function getCompleted(version) {
  try { return localStorage.getItem(STORAGE_KEY) === "1" && localStorage.getItem(VERSION_KEY) === String(version); }
  catch { return false; }
}
function markCompleted(version) {
  try { localStorage.setItem(STORAGE_KEY, "1"); localStorage.setItem(VERSION_KEY, String(version)); } catch {}
}

function SleepWakeText({ text, className = "" }) {
  return (
    <span className={className} aria-label={text}>
      {String(text).split("").map((char, i) => (
        <span key={`${char}-${i}`} className="navi-letter" style={{ "--delay": `${Math.min(i, 42) * 0.014}s` }} aria-hidden="true">
          {char === " " ? "\u00a0" : char}
        </span>
      ))}
    </span>
  );
}

function LeafField({ count = 22 }) {
  return (
    <div className="navi-leaf-field" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => {
        const angle = (i / count) * Math.PI * 2;
        const radius = 28 + (i % 5) * 10;
        return <span key={i} className="navi-leaf" style={{ "--delay": `${i * 0.035}s`, "--dx": `${Math.cos(angle) * radius}vw`, "--dy": `${Math.sin(angle) * radius}vh` }} />;
      })}
    </div>
  );
}

function CelebrationBurst() {
  const pieces = Array.from({ length: 52 }, (_, i) => {
    const angle = (i / 52) * Math.PI * 2;
    const radius = 150 + (i % 7) * 30;
    return { i, x: Math.cos(angle) * radius, y: Math.sin(angle) * radius, r: (i * 43) % 180 };
  });
  return (
    <div className="navi-burst" aria-hidden="true">
      {pieces.map((p) => <span key={p.i} style={{ "--x": `${p.x}px`, "--y": `${p.y}px`, "--r": `${p.r}deg`, "--i": p.i }} />)}
      <span className="navi-burst-emoji navi-burst-emoji-1">🎉</span>
      <span className="navi-burst-emoji navi-burst-emoji-2">🎊</span>
    </div>
  );
}

export default function LaunchExperience({ content, onComplete }) {
  const cfg = useMemo(() => {
    const cms = content?.launch_experience || {};
    const cmsVersion = String(cms.version || "");
    const version = cmsVersion && cmsVersion !== "2026.10" ? cmsVersion : DEFAULTS.version;
    return { ...DEFAULTS, ...cms, version };
  }, [content]);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const alreadyCompleted = useMemo(() => getCompleted(cfg.version), [cfg.version]);
  const [phase, setPhase] = useState(() => alreadyCompleted || cfg.enabled === false ? "done" : "intro");
  const [question, setQuestion] = useState(1);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [count, setCount] = useState(10);
  const [soundOn, setSoundOn] = useState(false);
  const [holdReady, setHoldReady] = useState(false);
  const inputRef = useRef(null);

  const logo = resolveMediaUrl(content?.hero?.logo_url || "/navi-care-logo.png");
  const waitingPhase = ["founderReveal", "cofounderReveal", "brandReveal", "celebrate", "vision"].includes(phase);

  useEffect(() => {
    if (phase !== "question") return undefined;
    const id = window.setTimeout(() => inputRef.current?.focus(), reduced ? 0 : 650);
    return () => window.clearTimeout(id);
  }, [phase, question, reduced]);

  // Every reveal is intentionally held. The visitor controls the story with a key/tap.
  useEffect(() => {
    setHoldReady(false);
    if (!waitingPhase) return undefined;
    const id = window.setTimeout(() => setHoldReady(true), reduced ? 100 : 2100);
    return () => window.clearTimeout(id);
  }, [phase, waitingPhase, reduced]);

  useEffect(() => {
    if (!waitingPhase) return undefined;
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (!holdReady) return;
      if (event.key === "Tab") return;
      event.preventDefault();
      advanceReveal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [waitingPhase, holdReady, phase]);

  function advanceReveal() {
    if (!holdReady) return;
    if (phase === "founderReveal") {
      setQuestion(2); setAnswer(""); setError(""); setPhase("question");
    } else if (phase === "cofounderReveal") setPhase("brandReveal");
    else if (phase === "brandReveal") setPhase("celebrate");
    else if (phase === "celebrate") setPhase("vision");
    else if (phase === "vision") setPhase("final");
  }

  useEffect(() => {
    if (!waitingPhase || !holdReady) return undefined;
    const onPointer = (event) => {
      if (event.target?.closest?.("button, input, textarea, select, a")) return;
      advanceReveal();
    };
    window.addEventListener("pointerdown", onPointer);
    return () => window.removeEventListener("pointerdown", onPointer);
  }, [waitingPhase, holdReady, phase]);

  useEffect(() => {
    if (phase !== "launching") return undefined;
    setCount(10);
    if (reduced) {
      const id = window.setTimeout(() => finish(), 900);
      return () => window.clearTimeout(id);
    }
    const timers = [];
    for (let n = 9; n >= 1; n -= 1) timers.push(window.setTimeout(() => setCount(n), (10 - n) * 1000));
    timers.push(window.setTimeout(() => setPhase("postLaunch"), 10000));
    return () => timers.forEach(window.clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, reduced]);

  function goLive() {
    markCompleted(cfg.version);
    setPhase("done");
    onComplete?.();
  }

  if (phase === "done" || cfg.enabled === false) return null;

  const submit = (e) => {
    e.preventDefault();
    const expected = question === 1 ? cfg.founder : cfg.cofounder;
    const accepted = question === 1
      ? [expected, "VOSURI NAVYA", "NAVYA VOSURI"]
      : [expected, "Surendra Y", "SURENDRA Y", "Surendra Yeddula", "SURENDRA YEDDULA"];
    if (!accepted.some((value) => normalize(answer) === normalize(value))) {
      setError("Almost there — try the name again.");
      return;
    }
    setError("");
    setPhase(question === 1 ? "founderReveal" : "cofounderReveal");
  };

  const questionText = question === 1
    ? "WHO FIRST IMAGINED NAVI CARE?"
    : "WHO TURNED THE IDEA INTO NAVI CARE?";

  return (
    <div className={`navi-launch navi-launch-${phase}`} role="dialog" aria-modal="true" aria-label="NAVI CARE launch experience">
      <div className="navi-launch-noise" aria-hidden="true" />
      <div className="navi-launch-brand-wash wash-left" aria-hidden="true" />
      <div className="navi-launch-brand-wash wash-right" aria-hidden="true" />
      <div className="navi-launch-glow glow-center" aria-hidden="true" />
      <div className="navi-launch-grid" aria-hidden="true" />

      <button type="button" className="navi-launch-sound" aria-label={soundOn ? "Mute launch sounds" : "Enable launch sounds"} onClick={() => setSoundOn((v) => !v)}>
        {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}<span>{soundOn ? "Sound on" : "Sound off"}</span>
      </button>

      {phase === "intro" && (
        <section className="navi-launch-panel navi-launch-intro">
          <div className="navi-suspense-mark" aria-hidden="true"><div className="navi-suspense-leaf" /><div className="navi-suspense-orbit orbit-a" /><div className="navi-suspense-orbit orbit-b" /></div>
          <div className="navi-intro-overline">THE STORY BEHIND NAVI CARE</div>
          <h1 className="navi-intro-title"><SleepWakeText text="WHO IS BEHIND IT?" /></h1>
          <p className="navi-intro-line">Discover the people who turned the idea into NAVI CARE.</p>
          <button type="button" className="navi-launch-start" onClick={() => setPhase("question")} autoFocus>BEGIN THE REVEAL <ArrowRight size={17} /></button>
        </section>
      )}

      {phase === "question" && (
        <section className="navi-launch-panel navi-launch-question">
          <div className="navi-question-progress"><span>0{question}</span><i /><span>02</span></div>
          <div className="navi-question-eyebrow">{question === 1 ? "THE IDEA" : "THE BUILD"}</div>
          <h1 className="navi-question-title"><SleepWakeText text={questionText} /></h1>
          <p className="navi-question-hint">Type the name you believe is behind this chapter.</p>
          <form onSubmit={submit} className="navi-answer-stage">
            <div className={`navi-answer-line ${error ? "is-error" : ""}`}>
              <input ref={inputRef} value={answer} onChange={(e) => { setAnswer(e.target.value); setError(""); }} placeholder="TYPE THE NAME" autoComplete="off" aria-label={question === 1 ? "Founder answer" : "Co-founder answer"} />
              <button type="submit" aria-label="Reveal answer"><ArrowRight size={25} /></button>
            </div>
            <div className={`navi-answer-error ${error ? "show" : ""}`} role="status">{error}</div>
          </form>
        </section>
      )}

      {phase === "founderReveal" && (
        <section className="navi-launch-panel navi-reveal-stage navi-hold-stage" onClick={advanceReveal}>
          <div className="navi-reveal-kicker"><span>✓</span> YES — THAT'S RIGHT</div>
          <div className="navi-reveal-copy">THE FIRST SPARK</div>
          <div className="navi-logo-reveal">
            <div className="navi-logo-aura" />
            {logo ? <img src={logo} alt="NAVI CARE Services" /> : <span>N</span>}
          </div>
          <h2><SleepWakeText text="NAVYA VOSURI" /></h2>
          <p>THE IDEA THAT STARTED NAVI CARE.</p>
          <div className="navi-applause-line">✦  THE STORY STARTS HERE  ✦</div>
          <div className={`navi-hold-hint ${holdReady ? "is-ready" : ""}`}>PRESS ANY KEY TO CONTINUE</div>
        </section>
      )}

      {phase === "cofounderReveal" && (
        <section className="navi-launch-panel navi-cofounder-reveal navi-hold-stage" onClick={advanceReveal}>
          <div className="navi-reveal-kicker"><span>✓</span> YES — THAT'S RIGHT</div>
          <div className="navi-reveal-copy">THE VISION BUILDER</div>
          <h2><SleepWakeText text="SURENDRA YEDDULA" /></h2>
          <p>THE PERSON WHO HELPED TURN THE IDEA INTO NAVI CARE.</p>
          <div className="navi-applause-line">✦  NOW WATCH THE BRAND COME TOGETHER  ✦</div>
          <div className={`navi-hold-hint ${holdReady ? "is-ready" : ""}`}>PRESS ANY KEY TO REVEAL THE BRAND</div>
        </section>
      )}

      {phase === "brandReveal" && (
        <section className="navi-launch-panel navi-brand-reveal navi-hold-stage" onClick={advanceReveal}>
          <LeafField count={28} />
          <div className="navi-brand-kicker">THE NAVI CARE BRAND REVEAL</div>
          <div className="navi-brand-single-stage">
            <div className="navi-brand-logo-travel">{logo ? <img src={logo} alt="NAVI CARE logo" /> : <span>N</span>}</div>
            <div className="navi-brand-name-travel"><SleepWakeText text="NAVI CARE" /></div>
          </div>
          <div className="navi-brand-services">Services</div>
          <div className="navi-brand-answer"><span>THE IDEA. THE VISION. THE CARE.</span><strong>SURENDRA YEDDULA</strong></div>
          <div className={`navi-hold-hint ${holdReady ? "is-ready" : ""}`}>PRESS ANY KEY TO CONTINUE</div>
        </section>
      )}

      {phase === "celebrate" && (
        <section className="navi-launch-panel navi-celebrate-stage navi-hold-stage" onClick={advanceReveal}>
          <CelebrationBurst />
          <div className="navi-reveal-kicker">CONGRATULATIONS</div>
          <h2><SleepWakeText text="YOU'VE MET THE PEOPLE BEHIND NAVI CARE." /></h2>
          <div className="navi-founders-line"><span>NAVYA VOSURI</span><b>×</b><span>SURENDRA YEDDULA</span></div>
          <div className="navi-celebrate-sub">THE IDEA. THE VISION. THE CARE.</div>
          <div className={`navi-hold-hint ${holdReady ? "is-ready" : ""}`}>PRESS ANY KEY TO CONTINUE</div>
        </section>
      )}

      {phase === "vision" && (
        <section className="navi-launch-panel navi-vision-stage navi-hold-stage" onClick={advanceReveal}>
          <div className="navi-vision-label">THE NAVI CARE VISION</div>
          <div className="navi-vision-scene">
            <div className="navi-vision-line"><span>YOUR CAR</span><b>↔</b><span>OUR CARE</span></div>
            <div className="navi-vision-door">@ YOUR DOOR STEP</div>
          </div>
          <div className="navi-vision-sub">CARE, WHERE YOUR CAR ALREADY IS.</div>
          <div className={`navi-hold-hint ${holdReady ? "is-ready" : ""}`}>PRESS ANY KEY TO CONTINUE</div>
        </section>
      )}

      {phase === "final" && (
        <section className="navi-launch-panel navi-final-stage">
          <div className="navi-final-kicker">YOUR MOMENT IS HERE</div>
          <h2><SleepWakeText text="YOUR DREAM STARTS HERE." /></h2>
          <div className="navi-final-names"><span>NAVYA VOSURI</span><b>×</b><span>SURENDRA YEDDULA</span></div>
          <div className="navi-final-brand">NAVI CARE <i>Services</i></div>
          <button type="button" className="navi-launch-open" onClick={() => setPhase("launching")}>LAUNCH YOUR WEBSITE <ArrowRight size={18} /></button>
        </section>
      )}

      {phase === "launching" && (
        <section className="navi-launch-panel navi-countdown-stage" aria-live="assertive">
          <div className="navi-countdown-top">NAVI CARE SERVICES</div>
          <div className="navi-countdown-number" key={count}><SleepWakeText text={String(count)} /></div>
          <div className="navi-countdown-caption">OPENING YOUR NEW STANDARD OF CAR CARE</div>
          <div className="navi-countdown-founders"><span>NAVYA VOSURI</span><b>×</b><span>SURENDRA YEDDULA</span></div>
          <div className="navi-launch-beam" aria-hidden="true" />
          <div className="navi-launch-leaf-rain" aria-hidden="true">{Array.from({ length: 36 }, (_, i) => <i key={i} style={{ "--left": `${(i / 36) * 100}%`, "--rotate": `${i * 19}deg`, "--delay": `${i * 0.055}s` }} />)}</div>
        </section>
      )}

      {phase === "postLaunch" && (
        <section className="navi-launch-panel navi-post-launch-stage">
          <div className="navi-post-launch-brand">NAVI CARE <i>Services</i></div>
          <div className="navi-post-line navi-post-line-1">NAVI CARE SERVICES ARE BUILT TO HELP YOU KEEP YOUR CAR CARE GOING.</div>
          <div className="navi-post-line navi-post-line-2">YOUR CONTINUED CARE STARTS HERE.</div>
          <div className="navi-post-line navi-post-line-3">YOUR CAR. YOUR LOCATION. OUR SERVICE — WE COME TO YOU.</div>
          <div className="navi-post-signature">NAVYA VOSURI <b>×</b> SURENDRA YEDDULA</div>
          <button type="button" className="navi-go-live" onClick={goLive}>GO LIVE <ArrowRight size={19} /></button>
        </section>
      )}
    </div>
  );
}
